import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { GroupMatch } from './group-match';
export { GroupMatch };
import { groupMatchSchema } from '@schemas/business-objects/group-match';
import { GroupMatchEntryFormArray } from './group-match-entry-form-group';
import { HorseFormGroup } from './horse-form-group';
import { HorseGroupFormGroup } from './horse-group-form-group';
import { UserFormGroup } from './user-form-group';
import type { PdfRenderResult } from '../interface-definitions/index';
import type { Match } from './match';

export class GroupMatchFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    groupName: SchemaFormControl;
    mareSizeInfluence: SchemaFormControl;
    sirePatternInfluence: SchemaFormControl;
    seed: SchemaFormControl;
    runTimestamp: SchemaFormControl;
    legacyId: SchemaFormControl;
    horseId: SchemaFormControl;
    horse: HorseFormGroup;
    groupId: SchemaFormControl;
    group: HorseGroupFormGroup;
    runById: SchemaFormControl;
    runBy: UserFormGroup;
    entries: GroupMatchEntryFormArray;
  };

  declare value: Partial<GroupMatch>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(groupMatchSchema, GroupMatch, data, options);
  }

  get object(): GroupMatch {
    return new GroupMatch(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'horse':
        return new HorseFormGroup();
      case 'group':
        return new HorseGroupFormGroup();
      case 'runBy':
        return new UserFormGroup();
      case 'entries':
        return new GroupMatchEntryFormArray();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<GroupMatch[]> {
    return GroupMatch.find(filter);
  }

  static async findOne(filter?: any): Promise<GroupMatch | null> {
    return GroupMatch.findOne(filter);
  }

  static async findById(id: number, filter?: any): Promise<GroupMatch> {
    return GroupMatch.findById(id, filter);
  }

  static async create(data: Partial<GroupMatch>): Promise<GroupMatch> {
    return GroupMatch.create(data as any);
  }

  static async updateById(
    id: number,
    updates: Partial<GroupMatch>,
  ): Promise<GroupMatch> {
    return GroupMatch.updateById(id, updates as any);
  }

  static async deleteById(id: number): Promise<boolean> {
    return GroupMatch.deleteById(id);
  }

  async consolidatedPdf(): Promise<PdfRenderResult> {
    const instance = new GroupMatch(this.getRawValue());
    return instance.consolidatedPdf();
  }

  static async countMatching(where?: Record<string, any>): Promise<number> {
    return GroupMatch.countMatching(where);
  }

  async reportPdf(): Promise<PdfRenderResult> {
    const instance = new GroupMatch(this.getRawValue());
    return instance.reportPdf();
  }

  static async run(
    horseId: number,
    groupId: number,
    mareSizeInfluence: number,
    sirePatternInfluence: number,
    seed?: number,
  ): Promise<GroupMatchFormGroup> {
    const result = await GroupMatch.run(
      horseId,
      groupId,
      mareSizeInfluence,
      sirePatternInfluence,
      seed,
    );
    const fg = new GroupMatchFormGroup();
    fg._populate(result);
    return fg;
  }

  async summaryPdf(): Promise<PdfRenderResult> {
    const instance = new GroupMatch(this.getRawValue());
    return instance.summaryPdf();
  }
}

export class GroupMatchFormArray extends PersistedFormArray {
  declare controls: GroupMatchFormGroup[];

  readonly entityName = 'GroupMatch' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(groupMatchSchema, GroupMatch, options);
  }

  protected override createItemGroup() {
    return new GroupMatchFormGroup();
  }

  get array(): GroupMatch[] {
    return this.controls.map((group: GroupMatchFormGroup) => group.object);
  }
}

export class GroupMatchPersistedArray extends PersistedArray<GroupMatch> {
  readonly entityName = 'GroupMatch' as const;

  constructor(options?: PersistedArrayOptions) {
    super(GroupMatch, options);
  }
}
